import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  makeStyles,
  IconButton,
  Link,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { useCookieConsent } from "../../contexts/CookieConsentContext";

/* ── Cookie icon SVG ─────────────────────────────────────────────────── */
const CookieIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M12.058 21.9996C16.973 21.9996 21.064 18.4936 21.942 13.8586C22.2 12.4966 21.593 12.5686 20.31 12.3436C19.4 12.1836 18.55 11.5666 18.233 10.5876C18.018 9.92162 17.938 9.89462 17.235 9.86562C15.786 9.80562 14.625 8.26862 15.203 7.02562C15.419 6.56162 15.412 6.50862 15.008 6.19362C14.301 5.64362 13.971 4.61662 14.098 3.62562C14.235 2.55862 14.315 2.17462 13.185 2.05462C7.457 1.44462 2 6.01962 2 11.9946C2 17.5196 6.503 21.9996 12.058 21.9996Z"
      stroke="white"
      strokeWidth="1.5"
    />
    <path
      d="M11.0061 12H10.9961M6.00609 10H5.99609M12.0061 18H11.9961"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 6L9 7M17 14L16 15M7 15L8 16"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ── Styles ──────────────────────────────────────────────────────────── */
const useStyles = makeStyles((theme) => ({
  /* Backdrop — both desktop and mobile */
  backdrop: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.55)",
    backdropFilter: "blur(4px)",
    zIndex: 9998,
    /* not clickable — pointer events disabled so click-through is blocked */
    pointerEvents: "all",
  },

  /* Card — Desktop: exact Figma spec */
  banner: {
    position: "fixed",
    zIndex: 9999,
    /* Gradient border + border-radius — padding-box/border-box trick */
    background: `
      linear-gradient(#131313, #131313) padding-box,
      linear-gradient(90deg,
        #87959C 1.44%, #89959C 7.69%, #8B949B 13.94%, #B1B3BB 21.15%,
        #C3C8CC 31.25%, #F1F2F4 38.94%, #D2DBE6 51.44%, #D2DBE6 63.46%,
        #9AB0B7 81.73%, #A5B7C0 94.71%
      ) border-box
    `,
    border: "0.5px solid transparent",
    borderRadius: "20px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.7)",
    padding: "28px 24px 24px",
    boxSizing: "border-box",

    /* Desktop dimensions */
    width: "530px",
    height: "auto",
    maxHeight: "calc(100vh - 48px)",
    bottom: "32px",
    top: "auto",
    left: "150px",
    overflowY: "auto",

    /* Mobile dimensions (Figma spec) */
    [theme.breakpoints.down("sm")]: {
      width: "348px",
      height: "auto",
      maxHeight: "calc(100vh - 32px)",
      top: "203px",
      bottom: "auto",
      left: "50%",
      transform: "translateX(-50%)",
      overflowY: "auto",
    },
    [theme.breakpoints.down("xs")]: {
      width: "calc(100% - 32px)",
      maxWidth: "348px",
      top: "50%",
      transform: "translate(-50%, -50%)",
    },
  },

  /* Close button — lives inside header row */
  closeBtn: {
    marginLeft: "auto",
    flexShrink: 0,
    color: "rgba(255,255,255,0.5)",
    padding: "4px",
    "&:hover": { color: "#fff", backgroundColor: "transparent" },
  },

  /* Header row — icon | title | close all on one line */
  header: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "14px",
  },
  title: {
    fontFamily: "'Roboto', sans-serif",
    fontWeight: 600,
    fontSize: "24px",
    color: "#ffffff",
    lineHeight: 1.3,
    flex: 1,
    [theme.breakpoints.down("sm")]: {
      fontFamily: "'TT Norms Pro', 'Roboto', sans-serif",
      fontWeight: 700,
      fontSize: "20px",
    },
  },

  /* Body text */
  description: {
    fontFamily: "'Roboto', sans-serif",
    fontSize: "16px",
    fontWeight: 400,
    color: "rgba(255,255,255,0.7)",
    lineHeight: "24px",
    marginBottom: "24px",
  },
  link: {
    color: "#FD3C84",
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },

  /* Buttons */
  acceptBtn: {
    width: "100%",
    height: "60px",                         /* desktop */
    borderRadius: "8px",
    backgroundColor: "#FD3C84",
    color: "#fff",
    fontFamily: "'Roboto', sans-serif",
    fontWeight: 600,
    fontSize: "20px",
    textTransform: "none",
    marginBottom: "10px",
    "&:hover": { backgroundColor: "#e04882" },
    [theme.breakpoints.down("sm")]: {
      height: "38px",
      fontWeight: 500,
      fontSize: "14px",                      /* mobile */
    },
  },
  rejectBtn: {
    width: "100%",
    height: "60px",                         /* desktop */
    borderRadius: "8px",
    fontWeight: 600,
    fontSize: "20px",
    backgroundColor: "transparent",
    color: "#ffffff",
    border: "1.5px solid #FE5A97",
    fontFamily: "'Roboto', sans-serif",
    textTransform: "none",
    marginBottom: "14px",
    "&:hover": {
      backgroundColor: "rgba(254,90,151,0.08)",
      border: "1.5px solid #FE5A97",

    },
    [theme.breakpoints.down("sm")]: {
      height: "38px",
      fontWeight: 500,
      fontSize: "14px",                   /* mobile */
    },
  },
  manageLink: {
    display: "block",
    textAlign: "center",
    fontFamily: "'Roboto', sans-serif",
    fontSize: "16px",
    fontWeight: 700,
    color: "#FD3C84",
    cursor: "pointer",
    textDecoration: "underline",
    "&:hover": { opacity: 0.8 },
  },
}));

/* ── Component ───────────────────────────────────────────────────────── */
interface CookieConsentBannerProps {
  onShowPreferences: () => void;
}

const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({
  onShowPreferences,
}) => {
  const classes = useStyles();
  const { acceptAll, rejectAll, hideBanner } = useCookieConsent();

  /* Lock body scroll while banner is visible */
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original === "hidden" ? "" : original;
    };
  }, []);

  const handleClose = () => {
    document.body.style.overflow = "";
    hideBanner();
  };

  const handleAcceptAll = () => {
    document.body.style.overflow = "";
    acceptAll();
  };

  const handleRejectAll = () => {
    document.body.style.overflow = "";
    rejectAll();
  };

  return (
    <>
      {/* Backdrop — blocks interaction with the page behind */}
      <Box className={classes.backdrop} />

      <Paper className={classes.banner} elevation={0}>
        {/* Header — icon | title | close on one line */}
        <Box className={classes.header}>
          <CookieIcon />
          <Typography className={classes.title}>
            Cookies make Sugar Cribs sweeter
          </Typography>
          <IconButton
            className={classes.closeBtn}
            size="small"
            onClick={handleClose}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {/* Description */}
        <Typography className={classes.description}>
          We use cookies to Enhance your experience, analyze site usage, and
          assist in our marketing efforts. By clicking "Accept All", you consent
          to our use of cookies. You can also{" "}
          <Link href="/cookie-policy" className={classes.link} target="_blank">
            learn more about our cookie policy
          </Link>{" "}
          or customize your preferences.
        </Typography>

        {/* Actions */}
        <Button
          className={classes.acceptBtn}
          onClick={handleAcceptAll}
          disableElevation
        >
          Accept All
        </Button>
        <Button
          className={classes.rejectBtn}
          onClick={handleRejectAll}
          disableElevation
        >
          Reject All
        </Button>
        <Link
          component="button"
          className={classes.manageLink}
          onClick={onShowPreferences}
        >
          Manage Preference
        </Link>
      </Paper>
    </>
  );
};

export default CookieConsentBanner;
